import { Component, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  LangChangeEvent,
  TranslateModule,
  TranslateService
} from '@ngx-translate/core';
import { lowerCase } from 'lodash';
import { Subject, takeUntil } from 'rxjs';
import { Session } from '@providers/session/session.service';

/**
 * Lang Component.
 *
 * @exports
 * @class LangComponent
 */
@Component({
  selector: 'app-lang',
  template: ``,
  imports: [TranslateModule]
})
export class LangComponent implements OnDestroy {
  private readonly router = inject(Router);
  private readonly session = inject(Session);
  private readonly translate = inject(TranslateService);

  destroyed = new Subject();

  /**
   * Creates an instance of LangComponent.
   *
   * @memberof LangComponent
   */
  constructor() {
    this.setup();
  }

  /**
   * Get current lang.
   *
   * @readonly
   * @type {string}
   * @memberof LangComponent
   */
  get lang(): string {
    return this.session.retrieve('lang') ?? 'en';
  }

  /**
   * Clean up.
   */
  ngOnDestroy(): void {
    this.destroyed.next(true);
    this.destroyed.complete();
  }

  /**
   * Initialize lang.
   *
   * @memberof LangComponent
   */
  setup(): void {
    this.translate.setFallbackLang('en');
    this.translate.use(this.lang);
    this.translate.onLangChange
      .pipe(takeUntil(this.destroyed))
      .subscribe((event: LangChangeEvent) => {
        this.session.store('lang', event.lang);
      });
    this.router.events.pipe(takeUntil(this.destroyed)).subscribe({
      /**
       * Set translation language.
       *
       * @param {Event} e Event.
       * @returns {void}
       */
      next: (e): void => {
        if (e instanceof NavigationEnd) {
          let appLang = this.session.lang;

          const mainLang: string = this.session.retrieve(
            this.session.keys.mainLang
          ) as string;

          // We need to disable 360 content that translated to indonesian.
          // the app will load content in english if user try to access the 360-v2 page.
          if (/report\/360-v2/i.test(e.url)) {
            if (this.translate.getCurrentLang() !== 'en') {
              this.session.store(this.session.keys.mainLang, this.session.lang);
              appLang = 'en';
            }
          } else if (/report\/premium/i.test(e.url)) {
            this.session.store(this.session.keys.mainLang, appLang);
            const reportLang: string = lowerCase(
              this.session.retrieve(
                this.session.keys.reportPremiumLanguage
              ) as string
            );
            appLang = reportLang || appLang;
          } else {
            appLang = mainLang || appLang;
            // clear main lang
            this.session.clear(this.session.keys.mainLang);
          }

          if (this.translate.getCurrentLang() !== appLang) {
            this.translate.use(appLang);
          }
        }
      }
    });
  }
}
